import {FormField} from '@components/system/FormField/FormField';
import React, {ReactElement, useEffect, useRef, useState} from 'react';
import {
  Controller,
  FieldErrors,
  SubmitErrorHandler,
  SubmitHandler,
  useForm
} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import './PatientForms.scss';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  Typography,
  createStyles,
  makeStyles
} from '@material-ui/core';
import SecondaryButton from '@components/system/ButtonField/SecondaryButton';
import CMSIcon from '@components/system/MyIcons/CMSIcon';
import {DEFAULT_BACKGROUND_COLOR, INPUT_LABEL_COLOR} from 'src/style/variable';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {useHistory, useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {State} from '@shared/store/reducers/Index';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {
  fetchPatientForm,
  uploadPatientForm
} from '@shared/services/Payment.service';
import {PDFDocument, rgb} from 'pdf-lib';
import NavRoutes from 'src/constants/route/NavRoutes';
import Loader from '@components/framework/loader/Loader';
import HelperUtils, {isValidDateCheck} from '@shared/utils/HelperUtils';
import ThanksLogo from '@components/system/MyIcons/ThanksLogo';
import ReactSignatureCanvas from 'react-signature-canvas';

const MedicareBeneficiaryForm = () => {
  const {t} = useTranslation();
  const clinicDetails: any = useSelector(
    (state: State) => state.payments.clinicDetails
  );
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography className="cover-letter-title">
          {t('PATIENT_MEDICARE_FORM.COVER_LETTER_TITLE')}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {clinicDetails?.clinic_group_details
          ?.form_medicare_beneficiary_html && (
          <div
            className="medicare-form-content"
            dangerouslySetInnerHTML={{
              __html:
                clinicDetails?.clinic_group_details
                  ?.form_medicare_beneficiary_html || ''
            }}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
};

/* eslint-disable camelcase */
interface IMedicareBeneficiaryFormFields {
  provider_name: string;
  practice_name: string;
  beneficiary_first_name: string;
  beneficiary_last_name: string;
  medicare_number: string;
  signature: any;
  date: string;
  signer_name: string;
  consent: boolean;
  date_of_birth: string;
}

/* eslint-enable camelcase */

const useSignatureBoardStyles = makeStyles(() =>
  createStyles({
    signCanvas: {
      width: '100%',
      backgroundColor: DEFAULT_BACKGROUND_COLOR,
      height: '5rem'
    },
    signPadActionButtonWrapper: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: '1rem',
      flexWrap: 'wrap',
      marginTop: '0.5rem',
      '& button': {
        marginLeft: 'auto'
      }
    }
  })
);

interface ISignatureBoard {
  onSign: (sign: string | null) => void;
  errorMessage?: string;
}
const SignatureBoard = ({onSign, errorMessage}: ISignatureBoard) => {
  const {t} = useTranslation();
  const sigCanvas = useRef<ReactSignatureCanvas>(null);
  const classes = useSignatureBoardStyles();

  const updateSignature = () => {
    if (sigCanvas.current) {
      const dataURL = sigCanvas.current
        .getTrimmedCanvas()
        .toDataURL('image/png');
      onSign(dataURL);
    }
  };

  const clearCanvas = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
    onSign(null);
  };

  useEffect(() => {
    if (sigCanvas.current) {
      const canvas = sigCanvas.current.getCanvas();
      if (canvas) {
        // Add event listeners for mouse and touch events
        canvas.addEventListener('pointerup', updateSignature);
        canvas.addEventListener('pointerleave', updateSignature);

        // Clean up event listeners on component unmount
        return () => {
          canvas.removeEventListener('pointerup', updateSignature);
          canvas.removeEventListener('pointerleave', updateSignature);
        };
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div>
      <ReactSignatureCanvas
        penColor="black"
        ref={sigCanvas}
        canvasProps={{
          className: classes.signCanvas
        }}
      />
      <Divider />
      <div className={classes.signPadActionButtonWrapper}>
        {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
        <SecondaryButton onClick={clearCanvas}>
          {t('COMMON.CLEAR')}
        </SecondaryButton>
      </div>
    </div>
  );
};

const PatientForms = (): ReactElement => {
  const {t} = useTranslation();
  const patientDetails: any = useSelector(
    (state: State) => state.payments.patientPaymentDetailsId
  );
  const {enqueueSnackbar} = useSnackbar();
  const {
    control,
    errors,
    handleSubmit,
    setValue,
    formState: {isSubmitting}
  } = useForm<IMedicareBeneficiaryFormFields>({
    mode: 'onChange',
    defaultValues: {
      date: HelperUtils.formatDisplayDate(new Date().toString())
    },
    resolver: values => {
      const validationErrors: FieldErrors<IMedicareBeneficiaryFormFields> = {};
      if (!values.provider_name) {
        validationErrors.provider_name = {
          type: 'required',
          message: t(
            'PATIENT_MEDICARE_FORM.PROVIDER_NAME_OR_MEDICAL_GROUP_REQUIRED_ERROR'
          )
        };
      }
      if (!values.practice_name) {
        validationErrors.practice_name = {
          type: 'required',
          message: t('PATIENT_MEDICARE_FORM.PRACTICE_NAME_REQUIRED_ERROR')
        };
      }
      if (!values.beneficiary_first_name) {
        validationErrors.beneficiary_first_name = {
          type: 'required',
          message: t(
            'PATIENT_MEDICARE_FORM.BENEFICIARY_FIRST_NAME_REQUIRED_ERROR'
          )
        };
      }
      if (!values.beneficiary_last_name) {
        validationErrors.beneficiary_last_name = {
          type: 'required',
          message: t(
            'PATIENT_MEDICARE_FORM.BENEFICIARY_LAST_NAME_REQUIRED_ERROR'
          )
        };
      }
      if (!values.medicare_number) {
        validationErrors.medicare_number = {
          type: 'required',
          message: t('PATIENT_MEDICARE_FORM.MEDICARE_NUMBER_REQUIRED_ERROR')
        };
      }
      if (!values.signature) {
        validationErrors.signature = {
          type: 'required',
          message: t('PATIENT_MEDICARE_FORM.SIGNATURE_REQUIRED_ERROR')
        };
      }
      if (!values.date) {
        validationErrors.date = {
          type: 'required',
          message: t('PATIENT_MEDICARE_FORM.DATE_REQUIRED_ERROR')
        };
      }
      if (values.date_of_birth && !isValidDateCheck(values.date_of_birth)) {
        validationErrors.date_of_birth = {
          type: 'required',
          message: t('PATIENT_MEDICARE_FORM.INVALID_DATE')
        };
      }
      if (!values.signer_name) {
        validationErrors.signer_name = {
          type: 'required',
          message: t('PATIENT_MEDICARE_FORM.SIGNER_NAME_REQUIRED_ERROR')
        };
      }
      if (!values.consent) {
        validationErrors.consent = {
          type: 'required',
          message: t('PATIENT_MEDICARE_FORM.CONSENT_REQUIRED_ERROR')
        };
      }

      if (Object.keys(validationErrors).length) {
        return {
          values: {},
          errors: validationErrors
        };
      }
      return {
        values,
        errors: {}
      };
    }
  });
  const [showSuccessfulScreen, setShowSuccessfulScreen] = useState(false);

  useEffect(() => {
    if (patientDetails?.clinic?.name) {
      setValue('practice_name', patientDetails.clinic.name);
      setValue('provider_name', patientDetails.clinic.name);
    }
    if (
      Array.isArray(patientDetails?.insurance) &&
      patientDetails?.insurance.length > 0
    ) {
      setValue(
        'medicare_number',
        patientDetails.insurance?.[0]?.subscriber_member_no
      );
    }
  }, [patientDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  const params = useParams<{token: string}>();

  const isLoggedInToPatientPortal = useSelector(
    (state: State) => state.payments.isLogin
  );

  const history = useHistory();

  const isFirstRender = useRef(true);
  if (isFirstRender && !isLoggedInToPatientPortal) {
    const token = params?.token || '';
    history.push(`/${NavRoutes.PUBLIC.PAYMENTS_LOGIN}/pay/${token}`);
  }

  const clinicDetails: any = useSelector(
    (state: State) => state.payments.clinicDetails
  );

  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  const formFields = [
    {
      label: t('PATIENT_MEDICARE_FORM.PRACTICE_NAME_LABEL'),
      name: 'practice_name',
      placeholder: t('PATIENT_MEDICARE_FORM.PRACTICE_NAME_PLACEHOLDER'),
      required: true,
      type: 'text',
      disabled: true
    },
    {
      label: t('PATIENT_MEDICARE_FORM.PROVIDER_NAME_OR_MEDICAL_GROUP_LABEL'),
      name: 'provider_name',
      placeholder: t(
        'PATIENT_MEDICARE_FORM.PROVIDER_NAME_OR_MEDICAL_GROUP_PLACEHOLDER'
      ),
      required: true,
      type: 'text',
      disabled: true
    },
    {
      label: t('PATIENT_MEDICARE_FORM.BENEFICIARY_FIRST_NAME_LABEL'),
      name: 'beneficiary_first_name',
      placeholder: t(
        'PATIENT_MEDICARE_FORM.BENEFICIARY_FIRST_NAME_PLACEHOLDER'
      ),
      required: true,
      type: 'text'
    },
    {
      label: t('PATIENT_MEDICARE_FORM.BENEFICIARY_LAST_NAME_LABEL'),
      name: 'beneficiary_last_name',
      placeholder: t('PATIENT_MEDICARE_FORM.BENEFICIARY_LAST_NAME_PLACEHOLDER'),
      required: true,
      type: 'text'
    },
    {
      label: t('PATIENT_MEDICARE_FORM.DATE_OF_BIRTH'),
      name: 'date_of_birth',
      type: 'date',
      required: false,
      placeholder: t('PATIENT_MEDICARE_FORM.DATE_OF_BIRTH_PLACEHOLDER')
    },
    {
      label: t('PATIENT_MEDICARE_FORM.MEDICARE_NUMBER_LABEL'),
      name: 'medicare_number',
      placeholder: t('PATIENT_MEDICARE_FORM.MEDICARE_NUMBER_PLACEHOLDER'),
      required: true,
      type: 'text'
    },
    {
      label: t('PATIENT_MEDICARE_FORM.SIGNATURE_LABEL'),
      name: 'signature',
      type: 'signature',
      required: true
    },
    {
      label: t('PATIENT_MEDICARE_FORM.DATE_LABEL'),
      name: 'date',
      type: 'date',
      required: true,
      placeholder: t('PATIENT_MEDICARE_FORM.DATE_PLACEHOLDER'),
      disabled: true
    },
    {
      label: t('PATIENT_MEDICARE_FORM.SIGNER_NAME_LABEL'),
      name: 'signer_name',
      placeholder: t('PATIENT_MEDICARE_FORM.SIGNER_NAME_PLACEHOLDER'),
      required: true,
      type: 'text'
    },
    {
      label: t('PATIENT_MEDICARE_FORM.CONSENT_LABEL'),
      name: 'consent',
      type: 'single-checkbox',
      required: true
    }
  ];

  const createForm: SubmitHandler<IMedicareBeneficiaryFormFields> = async values => {
    const formPageNumber =
      clinicDetails?.clinic_group_details?.form_medicare_beneficiary_coordinates
        ?.page_number;
    // IF THERE IS NO CLINIC ID OR NO PAGE NUMBER, WE CAN'T UPLOAD THE FORM
    if (!patientDetails?.clinic?.id || typeof formPageNumber != 'number')
      return;
    try {
      const patientFormRes = await fetchPatientForm({
        params: {
          clinic_id: patientDetails.clinic.id
        }
      });

      // Load the existing PDF
      const pdfDoc = await PDFDocument.load(patientFormRes.data);

      // Get the first page of the PDF
      const pages = pdfDoc.getPages();

      const formPage = pages[formPageNumber];

      const formValues = {
        practice_name: values.practice_name,
        provider_name: values.provider_name,
        beneficiary_name: `${values.beneficiary_first_name} ${values.beneficiary_last_name}`,
        medicare_number: values.medicare_number,
        date: values.date,
        signer_name: values.signer_name,
        signature: values.signature,
        date_of_birth: values.date_of_birth
      };

      const inputFieldKeys = Object.keys(formValues);
      const inputFieldCoordinates =
        clinicDetails?.clinic_group_details
          ?.form_medicare_beneficiary_coordinates?.coordinates;

      inputFieldKeys.map(fieldKey => {
        const field: {
          x: number;
          y: number;
          width: number;
          height: number;
        } = inputFieldCoordinates?.[fieldKey];
        if (field) {
          formPage.drawRectangle({
            x: field.x,
            y: field.y,
            width: field.width,
            height: field.height,
            color: rgb(1, 1, 1)
          });
        }
      });

      for (const fieldKey of inputFieldKeys) {
        const value = formValues?.[fieldKey as 'beneficiary_name'];
        const field: {
          x: number;
          y: number;
          width: number;
          height: number;
        } = inputFieldCoordinates?.[fieldKey];

        // IF THE INPUT'S COORDINATE IS NOT THERE NO NEED TO DO ANYTHING
        if (!field) {
          continue;
        }
        if (fieldKey === 'signature') {
          const base64Image = value; // Replace with your base64 image
          const imageBytes = await fetch(base64Image).then(res =>
            res.arrayBuffer()
          );
          const signImageMaxHeight = field.height;
          const signImageMaxWidth = field.width;

          const image = await pdfDoc.embedPng(imageBytes);
          // Get image dimensions
          const imageWidth = image.width;
          const imageHeight = image.height;
          let scale = 1;
          if (
            image.height > signImageMaxHeight ||
            image.width > signImageMaxWidth
          ) {
            scale = Math.min(
              signImageMaxHeight / image.height,
              signImageMaxWidth / image.width
            );
            image.scale(scale);
          }

          // Draw the embedded image
          formPage.drawImage(image, {
            x: field.x,
            y: field.y,
            width: imageWidth * scale,
            height: imageHeight * scale
          });
        } else if (value) {
          formPage.drawText(value, {
            x: field.x + 5,
            y: field.y + field.height / 2 - 8,
            size: 16,
            color: rgb(0, 0, 0) // black color
          });
        }
      }

      // Serialize the PDFDocument to bytes
      const pdfBytes = await pdfDoc.save();

      // Create a Blob from the bytes
      const blob = new Blob([pdfBytes], {type: 'application/pdf'});

      // UPLOAD THE DATA
      const payload = new FormData();
      payload.append('patient_id', patientDetails?.id);
      payload.append('file', blob);
      payload.append('clinic_id', patientDetails.clinic.id);
      payload.append(
        'metadata',
        JSON.stringify({
          provider_name: values.provider_name,
          practice_name: values.practice_name,
          beneficiary_first_name: values.beneficiary_first_name,
          beneficiary_last_name: values.beneficiary_last_name,
          medicare_number: values.medicare_number,
          date: values.date,
          signer_name: values.signer_name,
          consent: values.consent,
          date_of_birth: values.date_of_birth
        })
      );
      await uploadPatientForm({payload});
      setShowSuccessfulScreen(true);
    } catch (err: any) {
      enqueueSnackbar(err?.message, SnackBarConfig('e'));
    }
  };

  const formErrorHandler: SubmitErrorHandler<IMedicareBeneficiaryFormFields> = formErrors => {
    const errorObjects = Object.values(formErrors);
    if (errorObjects.length > 0) {
      enqueueSnackbar(errorObjects?.[0]?.message, SnackBarConfig('e'));
    }
  };

  return (
    <div className="patient-forms-patient-portal-wrapper">
      {!showSuccessfulScreen ? (
        <div className="patient-forms-content-wrapper">
          <div className="title-wrapper">
            <CMSIcon className="logo" />
            <h1 className="title">
              {t('PATIENT_MEDICARE_FORM.CONFIRMATION_FORM_TITLE')}
            </h1>
          </div>
          <Divider className="title-separator" />
          <MedicareBeneficiaryForm />
          <Divider className="title-separator" />
          <p className="form-header">
            {t('PATIENT_MEDICARE_FORM.SIGN_FORM_CONFIRMATION_TEXT')}
          </p>
          <div className="form-fields-wrapper">
            {formFields.map(field => {
              if (field.required) {
                field.label = `${field.label} *`;
              }
              if (field.type === 'signature') {
                return (
                  <Controller
                    key={field.name}
                    name="signature"
                    control={control}
                    render={() => {
                      return (
                        <FormControl
                          className="sign-input-field"
                          error={!!errors?.[field.name as 'date']}>
                          <InputLabel
                            style={{
                              position: 'static',
                              transform: 'none',
                              marginBottom: '0.5rem',
                              fontSize: '1.1rem',
                              color: INPUT_LABEL_COLOR
                              // .MuiFormLabel-root.Mui-error to override the color
                            }}
                            shrink>
                            {field.label}
                          </InputLabel>
                          <div className="sign-wrapper">
                            <p className="sing-helper-text">
                              {t(
                                'PATIENT_MEDICARE_FORM.DRAW_YOUR_SIGNATURE_BELOW'
                              )}
                            </p>
                            <div className="sign-canvas-wrapper">
                              <SignatureBoard
                                onSign={signURL => {
                                  setValue('signature', signURL, {
                                    shouldValidate: true
                                  });
                                }}
                                errorMessage={
                                  errors?.[field.name as 'date']?.message
                                }
                              />
                            </div>
                          </div>
                        </FormControl>
                      );
                    }}
                  />
                );
              }
              return (
                <Controller
                  key={field.name}
                  name={field.name}
                  control={control}
                  defaultValue={null}
                  render={inputProps => (
                    <FormField
                      {...field}
                      {...inputProps}
                      error={errors?.[field.name as 'date']}
                    />
                  )}
                />
              );
            })}
          </div>
          {clinicDetails?.clinic_group_details
            ?.form_medicare_beneficiary_html_footer && (
            // ** here the div is the self closing ** //
            <div
              className="form-footer medicare-form-content"
              dangerouslySetInnerHTML={{
                __html:
                  clinicDetails?.clinic_group_details
                    ?.form_medicare_beneficiary_html_footer || ''
              }}
            />
          )}
          <div className="button-wrapper">
            <SecondaryButton
              className="cancel-button"
              onClick={() => history.goBack()}>
              {t('COMMON.CANCEL')}
            </SecondaryButton>
            <Button
              disabled={isSubmitting}
              variant="contained"
              color="primary"
              className="submit-button"
              onClick={handleSubmit(createForm, formErrorHandler)}
              startIcon={
                isSubmitting ? <Loader color="inherit" size={24} /> : null
              }>
              {t('COMMON.SUBMIT')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="patient-forms-success-wrapper">
          <h3 className="success-message">
            <div className="thanks-logo-wrapper">
              <ThanksLogo />
            </div>
            {t('PATIENT_MEDICARE_FORM.FORM_SUBMITTED_TEXT')}
          </h3>
          <Button
            variant="contained"
            color="primary"
            onClick={() => history.goBack()}>
            {t('PATIENT_MEDICARE_FORM.GO_HOME_LABEL')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PatientForms;
